import PropTypes from "prop-types";
import Modal from "@shared/v2/Modal";
import Button from "@shared/v2/Button";
import { TriangleExclamationSolidV6 } from "@shared/v2/Icomoon";
import Loading from "../../../Goals/components/Loader";

const WarningPill = ({ count, label }) => {
  if (count === 0) return null;

  return (
    <div className="tw-flex tw-items-center tw-gap-[8px] tw-rounded-sm tw-bg-semantic-yellow-5 tw-px-[8px] tw-py-[4px] tw-text-semantic-yellow-120 tw-text-12d">
      <span className="tw-flex tw-items-center tw-flex-shrink-0">
        <TriangleExclamationSolidV6 size="s" />
      </span>
      <span>
        {count} {count === 1 ? "contact" : "contacts"} {label(count)}
      </span>
    </div>
  );
};

WarningPill.propTypes = {
  count: PropTypes.number.isRequired,
  label: PropTypes.func.isRequired,
};

const MailingListOverviewModal = ({ show, onHide, onNext, isLoading, validationData }) => {
  if (!show) return null;

  const {
    maxSendable = Infinity,
    selectionCount = 0,
    acceptedCount = 0,
    noAddressCount = 0,
    undeliverableAddressCount = 0,
    unsupportedAddressTypeCount = 0,
    missingNameCount = 0,
    multipleAddressCount = 0,
    overLimit = false,
  } = validationData || {};

  const hasWarnings =
    noAddressCount > 0 ||
    undeliverableAddressCount > 0 ||
    unsupportedAddressTypeCount > 0 ||
    missingNameCount > 0;

  if (overLimit && !isLoading) {
    return (
      <Modal
        show={show}
        onHide={onHide}
        dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
        contentSize="medium"
      >
        <Modal.Header title="Recipient Limit Reached" closeButton className="tw-mb-32px" />
        <Modal.Body className="tw-mb-32px">
          <p className="tw-text-14d tw-text-gray-75">
            You&apos;ve selected {selectionCount.toLocaleString()} leads - at the moment, you can only send up
            to {maxSendable.toLocaleString()} handwritten cards at a time. Please revise your selection.
          </p>
        </Modal.Body>
        <Modal.Footer className="tw-flex tw-justify-between">
          <Button schema="tertiary" size="medium" onClick={onHide}>
            CANCEL
          </Button>
          <Button schema="primary" size="medium" onClick={onHide}>
            CLOSE
          </Button>
        </Modal.Footer>
      </Modal>
    );
  }

  return (
    <Modal
      show={show}
      onHide={onHide}
      dialogClassName="tw-flex tw-items-center tw-justify-center tw-h-full"
      contentSize="medium"
    >
      <Modal.Header title="Mailing List Overview" closeButton className="tw-mb-32px" />
      <Modal.Body className="tw-mb-32px">
        {isLoading ? (
          <div className="tw-flex tw-items-center tw-justify-center tw-h-[200px]">
            <Loading />
          </div>
        ) : (
          <div className="tw-flex tw-flex-col tw-gap-[16px]">
            {/* Recipients summary box */}
            <div className="tw-rounded-[8px] tw-border-2 tw-border-solid tw-border-theme-primary tw-p-[16px]">
              <div className="tw-mb-[12px]">
                <span className="tw-text-[28px] tw-font-normal tw-text-gray-75">
                  {acceptedCount.toLocaleString()}
                </span>
                <span className="tw-text-14d tw-text-gray-50">
                  {" "}
                  of {selectionCount.toLocaleString()} contacts will receive a card
                </span>
              </div>

              {hasWarnings && (
                <>
                  <div className="tw-mb-[12px] tw-h-0 tw-border-t-[1px] tw-border-b-0 tw-border-solid tw-border-gray-10" />
                  <p className="tw-mb-[12px] tw-text-12d tw-text-gray-50">
                    We&apos;ll exclude leads who don&apos;t have a deliverable mailing address or only have an
                    investment property address.
                  </p>
                  <div className="tw-flex tw-flex-wrap tw-gap-[8px]">
                    <WarningPill
                      count={noAddressCount}
                      label={(count) => `${count === 1 ? "has" : "have"} no address`}
                    />
                    <WarningPill
                      count={undeliverableAddressCount}
                      label={(count) =>
                        count === 1 ? "has an undeliverable address" : "have undeliverable addresses"
                      }
                    />
                    <WarningPill
                      count={unsupportedAddressTypeCount}
                      label={(count) =>
                        count === 1 ? "only has an Investment address" : "only have Investment addresses"
                      }
                    />
                    <WarningPill
                      count={missingNameCount}
                      label={(count) => `${count === 1 ? "is" : "are"} missing a first name`}
                    />
                  </div>
                </>
              )}
            </div>

            {/* Multiple addresses info */}
            {multipleAddressCount > 0 && (
              <div className="tw-rounded-[8px] tw-border tw-border-solid tw-border-gray-15 tw-p-[16px]">
                <p className="tw-mb-[12px] tw-text-14d tw-text-gray-75">
                  <span className="tw-font-bold">{multipleAddressCount}</span> leads have multiple addresses.
                  Cards will be sent to the first deliverable address in the following order:
                </p>
                <div className="tw-flex tw-items-center tw-justify-center tw-gap-[8px] tw-rounded-[4px] tw-bg-gray-5 tw-p-[12px] tw-text-14d tw-text-gray-75">
                  <span className="tw-font-semibold">Mailing Address</span>
                  <span className="tw-text-gray-30">&rarr;</span>
                  <span className="tw-font-semibold">Home Address</span>
                  <span className="tw-text-gray-30">&rarr;</span>
                  <span className="tw-font-semibold">Work Address</span>
                  <span className="tw-text-gray-30">&rarr;</span>
                  <span className="tw-font-semibold">Other</span>
                </div>
              </div>
            )}
          </div>
        )}
      </Modal.Body>
      <Modal.Footer className="tw-flex tw-justify-between">
        <Button schema="tertiary" size="medium" onClick={onHide}>
          CANCEL
        </Button>
        <Button schema="primary" size="medium" onClick={onNext} disabled={isLoading || acceptedCount === 0}>
          NEXT &rarr;
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

MailingListOverviewModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  onNext: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  validationData: PropTypes.shape({
    maxSendable: PropTypes.number,
    selectionCount: PropTypes.number,
    acceptedCount: PropTypes.number,
    skippedCount: PropTypes.number,
    noAddressCount: PropTypes.number,
    undeliverableAddressCount: PropTypes.number,
    unsupportedAddressTypeCount: PropTypes.number,
    missingNameCount: PropTypes.number,
    multipleAddressCount: PropTypes.number,
    overLimit: PropTypes.bool,
  }),
};

MailingListOverviewModal.defaultProps = {
  isLoading: false,
  validationData: null,
};

export default MailingListOverviewModal;
